// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: where a glass menu goes (GlassMenu). It always stays inside the safe area: never under the
// status bar or island, never under the home indicator. Below what was pressed, else above it. A
// held row (lift) that leaves room for neither slides up or down, as iOS context menus do, so
// the whole menu shows beside it; only a menu taller than the whole screen scrolls.

export interface Area {
	/** The visible height (the visual viewport: smaller while a keyboard is up). */
	height: number;
	width: number;
	/** Safe-area insets: status bar or island on top, home indicator below. */
	insetTop: number;
	insetBottom: number;
}

export interface Placement {
	top: number;
	left: number;
	maxHeight: number;
	origin: string;
	/** How far the held row moves (negative is up) so the menu fits beside it. */
	liftShift: number;
}

/** Space kept to the screen's safe edges, and between the menu and what it opened from. */
export const EDGE = 12;
export const GAP = 10;

export function placeMenu(
	rect: {top: number; bottom: number; right: number} | null,
	height: number,
	maxWidth: number,
	area: Area,
	/** The anchor is a held row lifted above the dim: it may move to make room. */
	lift = false,
): Placement {
	const width = Math.min(maxWidth, area.width - EDGE * 2);
	const right = rect ? Math.min(rect.right, area.width - EDGE) : area.width - EDGE;
	const left = Math.min(Math.max(right - width, EDGE), area.width - width - EDGE);
	const top0 = area.insetTop + EDGE;
	const bottom0 = area.height - area.insetBottom - EDGE;
	const room = bottom0 - top0;
	const down = (top: number, shift = 0): Placement => ({
		top,
		left,
		maxHeight: Math.max(0, bottom0 - top),
		origin: `${right - left}px 0`,
		liftShift: shift,
	});
	const up = (bottom: number, shift = 0): Placement => {
		const top = Math.max(top0, bottom - height);
		return {top, left, maxHeight: Math.max(0, bottom - top), origin: `${right - left}px 100%`, liftShift: shift};
	};
	if (!rect) return down(Math.min(top0 + 56, Math.max(top0, bottom0 - height)));
	const below = rect.bottom + GAP;
	const above = rect.top - GAP;
	if (below + height <= bottom0) return down(below);
	if (above - height >= top0) return up(above);
	if (lift) {
		const row = rect.bottom - rect.top;
		if (row + GAP + height <= room) {
			// Slide the row as little as it takes: up so the menu fits under it, or down so it fits above.
			const shiftUp = bottom0 - height - GAP - rect.bottom;
			const shiftDown = top0 + height + GAP - rect.top;
			return Math.abs(shiftUp) <= Math.abs(shiftDown)
				? down(rect.bottom + shiftUp + GAP, shiftUp)
				: up(rect.top + shiftDown - GAP, shiftDown);
		}
		// Taller than the screen beside the row: the row goes to the bottom, the menu fills the rest and scrolls.
		const shift = bottom0 - rect.bottom;
		return {
			top: top0,
			left,
			maxHeight: Math.max(0, rect.top + shift - GAP - top0),
			origin: `${right - left}px 100%`,
			liftShift: shift,
		};
	}
	// A button, not a row: the menu may cover it, so it fits whenever it fits on the screen.
	if (height <= room) return down(Math.min(Math.max(top0, below), bottom0 - height));
	return down(top0);
}

/** The least of a long message that stays in view above its menu. */
export const MIN_LIFT = 140;

export interface MessagePlacement {
	/** Top of the stack: the reactions, then the message, then the menu. */
	top: number;
	/** How much of the message shows; a longer one is cut off at the bottom. */
	bubbleMax: number;
	/** How tall the menu may be; a longer one scrolls. */
	menuMax: number;
}

/**
 * A long-pressed message (GlassMessageMenu): its quick reactions above it, its menu under it, as one
 * stack. The stack stays where the message is while all of it fits there, otherwise it moves to the
 * middle of the safe area. A long message is cut to leave the menu room, down to MIN_LIFT; a menu
 * that still does not fit scrolls.
 */
export function placeMessageMenu(
	anchor: {top: number; height: number},
	aboveHeight: number,
	menuHeight: number,
	area: Area,
): MessagePlacement {
	const top0 = area.insetTop + EDGE;
	const bottom0 = area.height - area.insetBottom - EDGE;
	const room = bottom0 - top0;
	const aboveGap = aboveHeight > 0 ? GAP : 0;
	const fixed = aboveHeight + aboveGap + GAP;
	const minBubble = Math.min(anchor.height, MIN_LIFT);
	const menuMax = Math.max(0, room - fixed - minBubble);
	const menuShown = Math.min(menuHeight, menuMax);
	const bubbleMax = Math.max(minBubble, room - fixed - menuShown);
	const stack = fixed + Math.min(anchor.height, bubbleMax) + menuShown;
	const inPlace = anchor.top - aboveHeight - aboveGap;
	const fits = inPlace >= top0 && inPlace + stack <= bottom0;
	return {top: fits ? inPlace : top0 + Math.max(0, (room - stack) / 2), bubbleMax, menuMax};
}
